import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT, PageSectionBuildingModule, SharedService, TranslateLoader } from '@cfp/core';
import { BuildingDetailsHeader } from '@cfp/features-shared/building';
import { BuildingMeasuresOverview } from '@cfp/features-shared/building-measures-overview';
import { LabelImprovement } from '@cfp/features-shared/label-improvement';
import { BuildingBusinessCaseCard } from '@cfp/shared/building/business-case';
import { BuildingSummaryCard } from '@cfp/shared/building/summary-card';
import { LabelGroupService } from '@cfp/shared/ui/label-group';
import { Card, FilterPipe, LoadingSpinner, OrderByModule, PrintableContent, SanitizeHtmlPipe } from '@cfp/ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BuildingLabelApi,
  BuildingMeasureTableItemDTO,
  BuildingMeasureTypeDTO,
  BuildingMeasuresListApi,
  BuildingReportsApi,
  BuildingsApi,
  CustomerByPortfolioService,
  LabelGroupDTO,
  MeasureStatusDTO,
  RoadmapApi
} from 'csr-asset-app';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  buildReportPdfFileName,
  formatBuildingAddressFileNamePart,
  formatReportFooterText,
  mapReportCustomerNameAndBusinessKey
} from '../../utils';
import { BuildingReportButtons } from '../building-report-buttons';
import { BuildingReportCover } from '../building-report-cover';
import { BuildingReportExplanationMeasurePanel } from '../building-report-explanation-measure-panel';
import { BuildingReportFooter, BuildingReportGraphs, BuildingReportRoadmapCard } from '../components';
import { BuildingReportContentService } from './building-report-content-service';

@Component({
  selector: 'cfp-building-report-content',
  templateUrl: './building-report-content.html',
  styleUrls: ['./building-report-content.scss'],
  imports: [
    LoadingSpinner,
    PrintableContent,
    BuildingDetailsHeader,
    PageSectionBuildingModule,
    BuildingReportButtons,
    BuildingSummaryCard,
    TranslateModule,
    LabelImprovement,
    BuildingMeasuresOverview,
    BuildingReportExplanationMeasurePanel,
    SanitizeHtmlPipe,
    DatePipe,
    OrderByModule,
    FilterPipe,
    BuildingReportRoadmapCard,
    Card,
    BuildingReportGraphs,
    BuildingReportFooter,
    BuildingBusinessCaseCard,
    BuildingReportCover
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuildingReportContent {
  protected readonly BuildingMeasureTypeDTO = BuildingMeasureTypeDTO;

  public readonly hideSelect = input<boolean>(false);
  public readonly hideGoToAllBuildings = input<boolean>(false);
  public readonly canDownloadReport = input<boolean>(false);
  public readonly showAllMeasuresButton = input<boolean>(false);
  public readonly hideDeleteBuilding = input<boolean>(false);
  public readonly showLabelInSummary = input<boolean>(true);
  public readonly showDescriptionInSummary = input<boolean>(true);
  public readonly showObjectTypeInSummary = input<boolean>(true);
  public readonly showSubObjectTypeInSummary = input<boolean>(false);
  public readonly showEnergyUsageInBusinessCase = input<boolean>(true);
  public readonly showOnlyExplanationForVisibleMeasures = input<boolean>(true);
  public readonly canEditMeasure = input<boolean>(false);
  public readonly showStatus = input<boolean>(false);
  public readonly isPdfServiceRequest = input<boolean>(false);
  public readonly showToolbar = input<boolean>(false);
  public readonly hasBuildingReportLicense = input<boolean>(false);
  public readonly hasRoadmapLicense = input<boolean>(false);
  public readonly reportCoverTitle = input<string>('');
  public readonly reportCoverCompany = input<string>('');
  public readonly portfolioId = input<number>();
  public readonly buildingIdFromInput = input<number>(undefined, { alias: 'buildingId' });
  public readonly buildingReportId = model<number>();
  public readonly buildingPublicSharedId = input<string>();
  public readonly hasBuildingReportFeedback = input<boolean>(false);

  public readonly showFeedback = output<void>();

  protected doneMeasureCount: number = 0;
  protected fiveYearMeasureCount: number = 0;
  protected readonly showFooter: boolean = false;

  protected readonly labelGroup = signal<LabelGroupDTO | undefined>(undefined);
  protected readonly internalBuildingId = signal<number | undefined>(undefined);

  protected readonly environment = inject(ENVIRONMENT);
  protected readonly buildingReportContentService = inject(BuildingReportContentService);
  protected readonly translateLoader = inject(TranslateLoader);
  private readonly translateService = inject(TranslateService);
  private readonly sharedService = inject(SharedService);
  private readonly labelGroupService = inject(LabelGroupService);
  private readonly buildingsApi = inject(BuildingsApi);
  private readonly buildingLabelApi = inject(BuildingLabelApi);
  private readonly buildingMeasuresListApi = inject(BuildingMeasuresListApi);
  private readonly roadmapApi = inject(RoadmapApi);
  private readonly buildingReportsApi = inject(BuildingReportsApi);
  private readonly customerByPortfolioService = inject(CustomerByPortfolioService);

  protected showDisclaimerLink =
    this.translateService.instant('consultantreport.disclaimer.link') !== 'consultantreport.disclaimer.link';

  protected readonly showExplanationPage = this.environment.components.buildingReportShowExplanationPage === true;

  private readonly buildingById = this.buildingsApi.v2BuildingsIdGet(() =>
    this.buildingPublicSharedId() ? undefined : this.internalBuildingId() || this.buildingIdFromInput()
  );

  private readonly buildingByPublicSharedId = this.buildingsApi.v1PublicBuildingsPublicSharedIdGet(() =>
    this.buildingPublicSharedId()
  );

  protected readonly building = computed(() => this.buildingById.value() ?? this.buildingByPublicSharedId.value());

  protected readonly buildingId = computed(
    () => this.internalBuildingId() || this.buildingIdFromInput() || this.building()?.id
  );

  protected readonly reportCustomer = toSignal(
    toObservable(this.building).pipe(
      switchMap((building) => {
        const portfolioId = building?.portfolio?.id;
        if (!portfolioId || this.environment.components.reportShowCustomerBusinessKeyInFooter !== true) {
          return of(undefined);
        }
        return this.customerByPortfolioService.v2CustomerByPortfolioIdGet(portfolioId);
      })
    )
  );

  protected readonly reportCustomerNameAndBusinessKey = computed(() =>
    mapReportCustomerNameAndBusinessKey(this.reportCustomer(), {
      showBusinessKeyInFooter: this.environment.components.reportShowCustomerBusinessKeyInFooter === true
    })
  );

  protected readonly reportFooterText = computed(() => formatReportFooterText(this.reportCustomerNameAndBusinessKey()));

  protected readonly reportPdfFileName = computed(() => {
    if (this.environment.components.reportShowCustomerBusinessKeyInFooter !== true) {
      return undefined;
    }

    const customerNameAndBusinessKey = this.reportCustomerNameAndBusinessKey();
    if (!customerNameAndBusinessKey.name) {
      return undefined;
    }

    return buildReportPdfFileName({
      customerNameAndBusinessKey,
      reportKind: 'building',
      address: formatBuildingAddressFileNamePart(this.building()?.address?.display),
      includeExtension: false
    });
  });

  protected readonly energyLabels = this.buildingLabelApi.v2BuildingsIdLabelsGet(() => this.buildingId());

  protected readonly businessCase = this.buildingsApi.v2BuildingsIdBusinesscaseGet(() => this.buildingId());

  protected readonly measures = this.buildingMeasuresListApi.v2BuildingsIdBuildingmeasuresGet(
    () => this.buildingId(),
    () => undefined
  );

  protected readonly roadmapBuildingDetails = this.roadmapApi.v2BuildingsIdRoadmapYearsGet(
    () => this.buildingId(),
    () => undefined
  );

  private readonly privateBuildingReport = this.buildingReportsApi.v2BuildingsBuildingIdReportsIdGet(
    () => (this.buildingPublicSharedId() ? undefined : this.buildingId()),
    () => this.buildingReportId()
  );

  private readonly publicBuildingReport = this.buildingReportsApi.v1PublicBuildingsPublicSharedIdReportsIdGet(
    () => this.buildingPublicSharedId(),
    () => this.buildingReportId()
  );

  protected readonly buildingReport = computed(
    () => this.privateBuildingReport.value() ?? this.publicBuildingReport.value()
  );

  private readonly partnershipLogoBlob = this.buildingReportsApi.v2BuildingsBuildingIdReportsIdPartnershiplogoImageGet(
    () => (this.buildingReport()?.hasPartnershipLogo ? this.buildingId() : undefined),
    () => (this.buildingReport()?.hasPartnershipLogo ? this.buildingReport()?.id : undefined)
  );

  protected readonly partnershipLogoAsBase64 = signal<string | undefined>(undefined);

  protected readonly labelGroups = computed(() => {
    const labels = this.energyLabels.value()?.energyLabels;
    return labels ? this.labelGroupService.getLabelGroups(labels) : undefined;
  });

  protected readonly buildingLabel = computed(() => {
    const labels = this.energyLabels.value()?.energyLabels;
    const group = this.labelGroup();
    return labels && group ? labels[group] : undefined;
  });

  protected readonly isLoading = computed(
    () =>
      !this.hasError() &&
      (this.buildingById.isLoading() ||
        this.buildingByPublicSharedId.isLoading() ||
        !this.building() ||
        !this.measures.value() ||
        this.energyLabels.isLoading() ||
        (!!this.labelGroups() && !this.labelGroup()))
  );

  protected readonly hasError = computed(
    () =>
      !!this.buildingById.error() ||
      !!this.buildingByPublicSharedId.error() ||
      !!this.measures.error() ||
      !!this.energyLabels.error()
  );

  protected readonly errorMessage = computed(() => {
    if (this.measures.error() || this.energyLabels.error()) {
      return this.buildingPublicSharedId() ? 'error.couldNotLoadPublicBuilding' : 'error.couldNotLoadBuilding';
    }
    if (this.buildingById.error() || this.buildingByPublicSharedId.error()) {
      return 'error.general';
    }
    return undefined;
  });

  protected readonly measureList = computed(() => this.measures.value());

  protected readonly buildingMeasures = computed(() => this.measureList()?.measureList);

  protected readonly buildingMeasuresForExplanation = computed(() => {
    const buildingMeasures = this.buildingMeasures();
    if (!buildingMeasures) {
      return undefined;
    }
    if (this.showOnlyExplanationForVisibleMeasures()) {
      return buildingMeasures
        .filter((measure) => measure.status !== MeasureStatusDTO.OUTOFSCOPE)
        .map((measure) => ({
          name: this.translateService.instant(`measure.name.${measure.code}`) as string,
          measure: measure
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((measure) => measure.measure);
    }
    return buildingMeasures;
  });

  protected readonly hasPlannedMeasureWithImplementationYear = computed(() => {
    const buildingMeasures = this.buildingMeasures();
    if (!buildingMeasures) {
      return undefined;
    }
    return (
      buildingMeasures.some((measure) => measure.status === MeasureStatusDTO.PLANNED && !!measure.implementationYear) ??
      false
    );
  });

  protected readonly plannedMeasuresOverviewTitleKey = computed(() => {
    if (!this.hasBuildingReportLicense() || !this.hasRoadmapLicense()) {
      return 'measures';
    }
    if (this.hasPlannedMeasureWithImplementationYear()) {
      return 'buildingReportPlannedMeasuresOverviewTitleWithoutYear';
    }
    return 'buildingReportPlannedMeasuresOverviewTitle';
  });

  protected readonly measureListFiltered = computed(() => {
    const measureListValue = this.measureList();
    if (!measureListValue) {
      return undefined;
    }

    const measureListFiltered = { ...measureListValue };
    measureListFiltered.measureList = measureListFiltered.measureList.filter((measure: BuildingMeasureTableItemDTO) => {
      return measure.code && !measure.implementationYear;
    });
    return measureListFiltered;
  });

  protected readonly showCfpLogo = computed(() => {
    const report = this.buildingReport();
    return report?.showCfpLogo || this.environment.components.consultantReportDefaultShowCfpLogo !== undefined;
  });

  protected readonly numberOfLogos = computed(() => {
    const report = this.buildingReport();
    const showCfpLogo = this.showCfpLogo();
    let numberOfLogos = 0;
    if (showCfpLogo) {
      numberOfLogos++;
    }

    if (report?.showEnergeticRegionLogo && this.environment.images.consultantReportPartnershipLogo) {
      numberOfLogos++;
    }

    if (report?.hasPartnershipLogo) {
      numberOfLogos++;
    }

    return numberOfLogos;
  });

  protected readonly rows = computed(() => {
    const roadmap = this.roadmapBuildingDetails.value();
    const measureListValue = this.measureList();
    if (!roadmap || !measureListValue) {
      return undefined;
    }

    return this.buildingReportContentService.combineRoadMapBuildingYearAndMeasureList(roadmap.years, measureListValue);
  });

  protected readonly footerDate = computed(() => {
    const report = this.buildingReport();
    return report?.checkDate ? new Date(report.checkDate) : undefined;
  });

  protected footerDateOrCurrentDate = computed(() => {
    const footerDate = this.footerDate();
    return footerDate ? footerDate : new Date();
  });

  constructor() {
    this.showFooter = this.translateService.instant('consultantreport.footer') !== 'consultantreport.footer';

    this.sharedService.reloadComponentObservable.pipe(takeUntilDestroyed()).subscribe(() => {
      this.buildingById.reload();
      this.buildingByPublicSharedId.reload();
      this.energyLabels.reload();
      this.businessCase.reload();
      this.measures.reload();
      this.roadmapBuildingDetails.reload();
      this.privateBuildingReport.reload();
      this.publicBuildingReport.reload();
    });

    effect(() => {
      const labelGroups = this.labelGroups();
      if (labelGroups && !this.labelGroup()) {
        this.labelGroup.set(this.labelGroupService.getDefaultLabelGroup(labelGroups));
      }
    });

    effect(() => {
      const building = this.building();
      if (this.buildingPublicSharedId() && building?.buildingReport?.id) {
        this.buildingReportId.set(building.buildingReport.id);
      }
    });

    effect(() => {
      const blob = this.partnershipLogoBlob.value();
      this.partnershipLogoAsBase64.set(blob ? URL.createObjectURL(blob) : undefined);
    });
  }

  protected getAllData(): void {
    this.buildingById.reload();
    this.buildingByPublicSharedId.reload();
    this.energyLabels.reload();
    this.businessCase.reload();
    this.measures.reload();
    this.roadmapBuildingDetails.reload();
    this.privateBuildingReport.reload();
    this.publicBuildingReport.reload();
  }

  protected labelGroupChange(labelGroup?: LabelGroupDTO): void {
    if (!labelGroup || this.labelGroup() === labelGroup) {
      return;
    }
    this.labelGroup.set(labelGroup);
  }
}
